import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

// utils
import { DISCOUNT_STATUS_STYLES } from "@/utils/statusStyles";
import { searchDiscounts } from "../utils/searchDiscounts";
import { formatDateTime } from "@/utils/formatDateTime";
import { fetchDiscounts } from "@/services/financeApi";
import { useBusiness } from "@hooks/useBusiness";

// reusable components
import { TableRow, TableCell } from "@/components/ui/table";
import TableActionsMenu from "@/components/ui/TableActionsMenu";
import { useStorePath } from "@hooks/useStorePath";

export default function useDiscountsTable() {
  const { businessId } = useBusiness();
  const navigate = useNavigate();
  const { storePath } = useStorePath();

  // Fetch the discounts and cache them using useQuery
  const {
    data: discounts = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    // Discounts have no Firestore collection - products carry prodDiscount but
    // there is no discount entity to list. Empty by design, not mock data.
    queryKey: ["discounts", businessId],
    queryFn: () => fetchDiscounts(businessId),
    enabled: Boolean(businessId),
    staleTime: 1000 * 60 * 5,
  });

  // Get the URL query string
  const [searchParams, setSearchParams] = useSearchParams();
  // Read the current search parameter in the URL
  const searchTerm = searchParams.get("search") || "";

  // Filtered based on the search term
  const filteredDiscounts = useMemo(() => {
    return searchDiscounts(discounts, searchTerm);
  }, [discounts, searchTerm]);

  // Update the search value in the URL
  const updateSearchTerm = useCallback((val) => {
    const newParams = new URLSearchParams(searchParams);
    val ? newParams.set("search", val) : newParams.delete("search");
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Navigate to disocunt view page
  const handleView = (discount) => {
    if (!discount?.id) return;
    navigate(storePath(`discounts/${discount.id}`));
  };

  const discountColumns = useMemo(() => [
    // Select option
    {
    id: "select",
    header: ({ table }) => (
        <input
        type="checkbox"
        className="rounded"
        checked={table.getIsAllPageRowsSelected()}
        onChange={(e) => table.toggleAllPageRowsSelected(!!e.target.checked)}
        aria-label="Select all"
        />
    ),
    cell: ({ row }) => (
        <input
        type="checkbox"
        className="rounded"
        checked={row.getIsSelected()}
        onChange={(e) => row.toggleSelected(!!e.target.checked)}
        aria-label="Select row"
        />
    ),
    enableSorting: false,
    enableHiding: false,
    },

    // Discount Name
    {
      accessorKey: "name",
      header: ({ column }) => (
        <button
          className="flex items-center gap-1"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Discount Name
          {column.getIsSorted() === "asc" && <span>↑</span>}
          {column.getIsSorted() === "desc" && <span>↓</span>}
        </button>
      ),
      cell: ({ row }) => row.original?.name || <span className="text-gray-400 italic">Unnamed</span>,
    },

    // Discount Type
    {
      accessorKey: "type",
      header: "Discount Type",
      cell: ({ row }) => row.original?.type || <span className="text-gray-400 italic">N/A</span>,
    },

    // Discount Code
    {
      accessorKey: "code",
      header: "Discount Code",
      cell: ({ row }) => row.original?.code || <span className="text-gray-400 italic">Auto</span>,
    },

    // Discount Status
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.original?.status?.toLowerCase();
        const style = DISCOUNT_STATUS_STYLES[status] || DISCOUNT_STATUS_STYLES["unknown"];

        return (
          <span className={`text-xs px-2 py-1 ${style.color}`}>
            {status?.charAt(0).toUpperCase() + status?.slice(1)}
          </span>
        );
      },
    },

    // Discount Start Date
    {
      accessorKey: "startDate",
      header: "Start Date",
      cell: ({ row }) => {
        const raw = row.original?.startDate;
        return raw ? formatDateTime(raw) : <span className="text-gray-400 italic">N/A</span>;
      },
    },

    // Discount End Date
    {
      accessorKey: "endDate",
      header: "End Date",
      cell: ({ row }) => {
        const raw = row.original?.endDate;
        return raw ? formatDateTime(raw) : <span className="text-gray-400 italic">N/A</span>;
      },
    },

    // Actions
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const discount = row.original;
        const actions = [
          {
            label: "Edit Discount",
            onClick: () => handleView(discount),
          },
        ];
        return <TableActionsMenu actions={actions} />;
      },
      enableSorting: false,
      enableHiding: false,
    }
  ], []);

  const renderDiscountRow = useCallback((row) => {
    const discount = row.original;

    return (
      <TableRow key={row.id} onClick={() => handleView(discount)} className="cursor-pointer hover:bg-[#f6f6ff] transition-colors">
        {row.getVisibleCells().map((cell) => (
          <TableCell className="px-4 py-3 text-[13px] font-medium" key={cell.id}>
            {cell.getContext().column.columnDef.cell
              ? cell.getContext().column.columnDef.cell(cell.getContext())
              : cell.renderCell()}
          </TableCell>
        ))}
      </TableRow>
    );
    
  }, [handleView]);

  return {
    allDiscounts: discounts,
    discounts: filteredDiscounts,
    searchTerm,
    updateSearchTerm,
    refetch,
    discountColumns,
    renderDiscountRow,
    isLoading,
    isError,
  };
}